import { ReadMoreLink } from '@/components/common/ReadMoreLink'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { usePage } from '@/components/providers/PageProvider'
import { type RecType } from '@/defs/rec'
import { useCardsRec } from '@/hooks/useCardsRec'
import { formatSalt } from '@/utils/formatUtil'
import { getRecName } from '@/utils/recUtil'

import { StatCommonBox } from '../../StatCommon/StatCommonBox/StatCommonBox'
import { StatCommonFigure } from '../../StatCommon/StatCommonFigure/StatCommonFigure'

type Props = {
  isInView?: boolean
  rec: RecType
}

export const StatRecSalt = (props: Props) =>
  props.rec.saltRec !== undefined ? <StatRecSaltRec {...props} /> : <StatRecSaltGlobal {...props} />

const StatRecSaltRec = ({ isInView, rec }: Props) => {
  const { page } = usePage()
  const subject = page?.metadata?.subject
  const label = subject ? `Salt (${subject})` : 'Yearly Salt'

  return <StatRecSaltInner isInView={isInView} label={label} rec={rec} salt={rec.saltRec} subject={subject} />
}

const StatRecSaltGlobal = ({ isInView = true, rec }: Props) => {
  const { card, loading } = useCardsRec({ enabled: isInView, rec })

  if (!isInView) return <StatCommonBox isBlank label='Salt' />
  if (loading) return <StatCommonBox label='Salt' loading />

  return <StatRecSaltInner isInView={isInView} label='Salt' rec={rec} salt={card?.salt} />
}

type StatRecSaltInnerProps = {
  isInView?: boolean
  label: string
  rec: RecType
  salt?: number
  subject?: string
}

const StatRecSaltInner = ({ isInView, label, rec, salt, subject }: StatRecSaltInnerProps) => {
  const fullName = getRecName(rec)

  const tooltip = (
    <>
      <h2>
        <IconWithLabel iconBefore='salt'>Salt Score</IconWithLabel>
      </h2>
      <p>
        <strong>Salt scores</strong> are voted on annually by the community, on a scale of 0 (least salty) to 4 (most
        salty).{' '}
        {salt !== undefined ? (
          <>
            <strong>{fullName}</strong> has a salt score of <strong>{formatSalt(salt)}</strong>
            {subject ? <span>{` in ${subject}`}</span> : <></>}
            <span>.</span>
          </>
        ) : (
          <>
            <strong>{fullName}</strong>
            <span> has not been voted on.</span>
          </>
        )}
      </p>
      <p>
        <ReadMoreLink as='LinkOutbound' href='/top/salt' />
      </p>
    </>
  )

  return (
    <StatCommonBox isInView={isInView} label={label} popover={tooltip}>
      <StatCommonFigure>
        <IconWithLabel iconBefore='salt' iconBeforeMargin='xs' iconBeforeSize='xs'>
          {formatSalt(salt)}
        </IconWithLabel>
      </StatCommonFigure>
    </StatCommonBox>
  )
}
